"use client";

import {
	type PointerEvent as ReactPointerEvent,
	type RefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { cn } from "../../../lib";
import {
	clampThumbDragVector,
	resolveThumbDirectionFromVector,
	resolveThumbDragMagnitude,
	THUMB_ANALOG_INNER_DEADZONE,
	THUMB_CONTROL_MAX_DRAG_PX,
	THUMB_MOVEMENT_DIRECTION_VECTORS,
	THUMB_MOVEMENT_DIRECTIONS,
	type ThumbMovementDirection,
} from "../lib/thumb-movement-direction";

export interface ThumbMovementDriveState {
	direction: ThumbMovementDirection;
	magnitude: number;
	/** Normalized stick (−1..1, +y down) for continuous 360° facing. */
	vector?: { x: number; y: number };
}

export interface ThumbMovementVisualState {
	thumbOffset: { dx: number; dy: number };
	activeDirection: ThumbMovementDirection | null;
}

/** Continuous analog stick vector in -1..1 (screen space, +y down). */
export interface ThumbAnalogVector {
	x: number;
	y: number;
}

export interface ThumbMovementControlsProps {
	className?: string;
	disabled?: boolean;
	onDriveChange?: (state: ThumbMovementDriveState | null) => void;
	onDriveEnd?: () => void;
	onPointerInteractionChange?: (isActive: boolean) => void;
	/** Reflects keyboard or automatic character movement when the user is not dragging. */
	syncedVisual?: ThumbMovementVisualState | null;
}

export interface EphemeralThumbMovementLayerProps {
	enabled: boolean;
	className?: string;
	/** Override default `ephemeral-thumb-movement-layer` test id (Studio Play). */
	"data-testid"?: string;
	/**
	 * Element that owns pointer hit-testing + capture (default: parent of the
	 * layer root). Pass the PanZoom viewport when the pad is elevated to an
	 * untransformed overlay so scale/pan of scene content does not skew it.
	 */
	interactionHostRef?: RefObject<Element | null>;
	/**
	 * Continuous analog output for free locomotion (preferred for scenes).
	 * Emits `null` on release / disable.
	 */
	onAnalogChange?: (vector: ThumbAnalogVector | null) => void;
	/** Discrete 8-way drive (same contract as docked controls). */
	onDriveChange?: (state: ThumbMovementDriveState | null) => void;
}

const CONTROL_SIZE_PX = 128;
const OUTER_RADIUS_PX = 56;
const THUMB_RADIUS_PX = 22;
const MAX_DRAG_PX = THUMB_CONTROL_MAX_DRAG_PX;
/** Appear / dismiss — transform+opacity only, strong ease-out. */
const EPHEMERAL_MOTION_MS = 180;
const EPHEMERAL_EASE = "cubic-bezier(0.23, 1, 0.32, 1)";

const THUMB_POINTER_BLOCK_SELECTOR = [
	"[data-scene-thumb-ignore]",
	"[data-thumb-ignore]",
	"button",
	"a",
	"input",
	"textarea",
	"select",
	"label",
	"summary",
	'[role="button"]',
	'[role="slider"]',
	'[role="menuitem"]',
	'[role="tab"]',
	'[role="switch"]',
	'[role="checkbox"]',
	'[role="radio"]',
	'[role="combobox"]',
	'[role="option"]',
	'[contenteditable="true"]',
	".sq-octagon-button-host",
].join(",");

/** True when the pointer is on chrome that must keep the click (rails, octagons, etc.). */
export function isThumbPointerBlockedTarget(
	target: EventTarget | null,
	host?: Element | null,
): boolean {
	if (!(target instanceof Element)) {
		return true;
	}
	if (target.closest(THUMB_POINTER_BLOCK_SELECTOR)) {
		return true;
	}
	// Anything visually above the host (rails / drawers / overlays) owns the hit.
	if (host && !host.contains(target)) {
		return true;
	}
	return false;
}

/**
 * Prefer topmost hit under the cursor so overlays outside the host (rails,
 * exit/restart octagons) are never stolen by a scene hang underneath.
 */
export function shouldStartThumbAtClientPoint(
	host: Element,
	clientX: number,
	clientY: number,
): boolean {
	if (typeof document === "undefined") {
		return false;
	}
	const top = document.elementFromPoint(clientX, clientY);
	if (!(top instanceof Element)) {
		return false;
	}
	if (!host.contains(top)) {
		return false;
	}
	return !isThumbPointerBlockedTarget(top, host);
}

function directionAngleDeg(direction: ThumbMovementDirection): number {
	const vector = THUMB_MOVEMENT_DIRECTION_VECTORS[direction];
	return (Math.atan2(vector.y, vector.x) * 180) / Math.PI;
}

function ThumbDirectionArrow({
	direction,
	active,
	center,
	outerRadius,
}: {
	direction: ThumbMovementDirection;
	active: boolean;
	center: number;
	outerRadius: number;
}) {
	const angleRad = (directionAngleDeg(direction) * Math.PI) / 180;
	const arrowCenterRadius = outerRadius - 9;
	const x = center + Math.cos(angleRad) * arrowCenterRadius;
	const y = center + Math.sin(angleRad) * arrowCenterRadius;
	const rotation = directionAngleDeg(direction) + 90;
	const scale = active ? 1.15 : 0.88;

	return (
		<g
			transform={`translate(${x} ${y}) rotate(${rotation}) scale(${scale})`}
			className="transition-[opacity,transform] duration-150 ease-out"
			style={{ opacity: active ? 1 : 0.35 }}
			aria-hidden
		>
			<path
				d="M0 -4 C6.5 -2.5 9 1 10 5.5 L0 2 L-10 5.5 C-9 1 -6.5 -2.5 0 -4 Z"
				className="transition-colors duration-150 ease-out"
				fill={
					active ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.55)"
				}
			/>
		</g>
	);
}

interface ThumbMovementPadFaceProps {
	thumbOffset: { dx: number; dy: number };
	activeDirection: ThumbMovementDirection | null;
	isDragging: boolean;
	className?: string;
	/**
	 * Ephemeral appear/dismiss. Transform-only (no opacity on the blur plate)
	 * so backdrop-filter never flickers sharp→blurred.
	 */
	ephemeralPhase?: "hidden" | "enter-from" | "in" | "out";
}

function ThumbMovementPadFace({
	thumbOffset,
	activeDirection,
	isDragging,
	className,
	ephemeralPhase,
}: ThumbMovementPadFaceProps) {
	const center = CONTROL_SIZE_PX / 2;
	const isEphemeral = Boolean(ephemeralPhase);
	const ephemeralCollapsed =
		ephemeralPhase === "hidden" ||
		ephemeralPhase === "out" ||
		ephemeralPhase === "enter-from";
	const ephemeralTransitionOff = ephemeralPhase === "enter-from";
	const magnitude = resolveThumbDragMagnitude(
		thumbOffset.dx,
		thumbOffset.dy,
		MAX_DRAG_PX,
	);
	/** Ephemeral: dim ring/arrows; knob stays full strength. */
	const chromeOpacity = isEphemeral ? 0.42 : 1;

	return (
		<div
			className={cn(
				"relative touch-none select-none",
				ephemeralPhase &&
					"motion-reduce:!scale-100 motion-reduce:!transition-none",
				className,
			)}
			style={{
				width: CONTROL_SIZE_PX,
				height: CONTROL_SIZE_PX,
				...(ephemeralPhase
					? {
							// Never opacity-animate this host — backdrop-blur flickers if we do.
							opacity: ephemeralPhase === "hidden" ? 0 : 1,
							transform: ephemeralCollapsed
								? "scale(0.92)"
								: "scale(1)",
							transitionProperty: ephemeralTransitionOff
								? "none"
								: "transform",
							transitionDuration: `${EPHEMERAL_MOTION_MS}ms`,
							transitionTimingFunction: EPHEMERAL_EASE,
						}
					: undefined),
			}}
			aria-hidden
		>
			{/* Soft ground shadow */}
			<div
				className="pointer-events-none absolute inset-[10%] rounded-full bg-black/35 blur-md"
				style={{ opacity: chromeOpacity }}
				aria-hidden
			/>
			{/*
			 * Frosted plate: blur layer stays at full opacity (filter stability);
			 * tint/border sit on top at chromeOpacity for ephemeral.
			 */}
			<div
				className="pointer-events-none absolute inset-0 rounded-full backdrop-blur-md"
				aria-hidden
			/>
			<div
				className={cn(
					"pointer-events-none absolute inset-0 rounded-full",
					"border border-white/25 bg-white/12",
					"shadow-[inset_0_1px_0_rgba(255,255,255,0.28),0_8px_24px_rgba(0,0,0,0.35)]",
				)}
				style={{ opacity: chromeOpacity }}
				aria-hidden
			/>
			<svg
				className="pointer-events-none absolute inset-0"
				width={CONTROL_SIZE_PX}
				height={CONTROL_SIZE_PX}
				viewBox={`0 0 ${CONTROL_SIZE_PX} ${CONTROL_SIZE_PX}`}
				style={{ opacity: chromeOpacity }}
				aria-hidden="true"
			>
				{THUMB_MOVEMENT_DIRECTIONS.map((direction) => (
					<ThumbDirectionArrow
						key={direction}
						direction={direction}
						active={activeDirection === direction}
						center={center}
						outerRadius={OUTER_RADIUS_PX}
					/>
				))}
			</svg>
			{/* Knob — full opacity even when ephemeral chrome is dimmed */}
			<div
				className={cn(
					"pointer-events-none absolute rounded-full",
					"border border-white/55 bg-gradient-to-b from-white to-white/85",
					"shadow-[0_2px_8px_rgba(0,0,0,0.28),inset_0_1px_0_rgba(255,255,255,0.95),inset_0_-2px_4px_rgba(0,0,0,0.1)]",
					isDragging
						? "transition-none"
						: "transition-[transform] duration-200 ease-out",
				)}
				style={{
					width: THUMB_RADIUS_PX * 2,
					height: THUMB_RADIUS_PX * 2,
					left: center - THUMB_RADIUS_PX,
					top: center - THUMB_RADIUS_PX,
					transform: `translate(${thumbOffset.dx}px, ${thumbOffset.dy}px) scale(${
						isDragging ? 1 + magnitude * 0.06 : 1
					})`,
				}}
				aria-hidden
			/>
		</div>
	);
}

function emitFromOffset(
	dx: number,
	dy: number,
	handlers: {
		onDriveChange?: (state: ThumbMovementDriveState | null) => void;
		onAnalogChange?: (vector: ThumbAnalogVector | null) => void;
	},
): {
	offset: { dx: number; dy: number };
	direction: ThumbMovementDirection | null;
} {
	const clamped = clampThumbDragVector(dx, dy, MAX_DRAG_PX);
	const direction = resolveThumbDirectionFromVector(clamped.dx, clamped.dy);
	const magnitude = resolveThumbDragMagnitude(
		clamped.dx,
		clamped.dy,
		MAX_DRAG_PX,
	);

	if (magnitude < THUMB_ANALOG_INNER_DEADZONE) {
		handlers.onDriveChange?.(null);
		handlers.onAnalogChange?.(null);
		return { offset: clamped, direction: null };
	}

	handlers.onAnalogChange?.({
		x: clamped.dx / MAX_DRAG_PX,
		y: clamped.dy / MAX_DRAG_PX,
	});
	if (direction) {
		handlers.onDriveChange?.({
			direction,
			magnitude,
			vector: {
				x: clamped.dx / MAX_DRAG_PX,
				y: clamped.dy / MAX_DRAG_PX,
			},
		});
	} else {
		handlers.onDriveChange?.(null);
	}
	return { offset: clamped, direction };
}

/**
 * Docked thumb pad (module map) — always visible when mounted.
 */
export function ThumbMovementControls({
	className,
	disabled = false,
	onDriveChange,
	onDriveEnd,
	onPointerInteractionChange,
	syncedVisual = null,
}: ThumbMovementControlsProps) {
	const center = CONTROL_SIZE_PX / 2;
	const containerRef = useRef<HTMLDivElement>(null);
	const [pointerThumbOffset, setPointerThumbOffset] = useState({
		dx: 0,
		dy: 0,
	});
	const [pointerActiveDirection, setPointerActiveDirection] =
		useState<ThumbMovementDirection | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const draggingRef = useRef(false);
	const activePointerIdRef = useRef<number | null>(null);

	const thumbOffset = isDragging
		? pointerThumbOffset
		: (syncedVisual?.thumbOffset ?? { dx: 0, dy: 0 });
	const activeDirection = isDragging
		? pointerActiveDirection
		: (syncedVisual?.activeDirection ?? null);

	const resolveOffsetFromPointer = useCallback(
		(clientX: number, clientY: number) => {
			const container = containerRef.current;
			if (!container) {
				return { dx: 0, dy: 0 };
			}
			const rect = container.getBoundingClientRect();
			const localX = clientX - rect.left - center;
			const localY = clientY - rect.top - center;
			return clampThumbDragVector(localX, localY, MAX_DRAG_PX);
		},
		[center],
	);

	const emitDrive = useCallback(
		(dx: number, dy: number) => {
			const next = emitFromOffset(dx, dy, { onDriveChange });
			setPointerThumbOffset(next.offset);
			setPointerActiveDirection(next.direction);
		},
		[onDriveChange],
	);

	const handlePointerDown = useCallback(
		(event: ReactPointerEvent<HTMLDivElement>) => {
			if (disabled) {
				return;
			}
			event.preventDefault();
			draggingRef.current = true;
			setIsDragging(true);
			onPointerInteractionChange?.(true);
			activePointerIdRef.current = event.pointerId;
			event.currentTarget.setPointerCapture(event.pointerId);

			const offset = resolveOffsetFromPointer(
				event.clientX,
				event.clientY,
			);
			emitDrive(offset.dx, offset.dy);
		},
		[
			disabled,
			emitDrive,
			onPointerInteractionChange,
			resolveOffsetFromPointer,
		],
	);

	const handlePointerMove = useCallback(
		(event: ReactPointerEvent<HTMLDivElement>) => {
			if (
				!draggingRef.current ||
				disabled ||
				activePointerIdRef.current !== event.pointerId
			) {
				return;
			}
			const offset = resolveOffsetFromPointer(
				event.clientX,
				event.clientY,
			);
			emitDrive(offset.dx, offset.dy);
		},
		[disabled, emitDrive, resolveOffsetFromPointer],
	);

	const finishDrag = useCallback(
		(event: ReactPointerEvent<HTMLDivElement>) => {
			if (
				!draggingRef.current ||
				activePointerIdRef.current !== event.pointerId
			) {
				return;
			}
			draggingRef.current = false;
			setIsDragging(false);
			onPointerInteractionChange?.(false);
			activePointerIdRef.current = null;
			if (event.currentTarget.hasPointerCapture(event.pointerId)) {
				event.currentTarget.releasePointerCapture(event.pointerId);
			}
			setPointerThumbOffset({ dx: 0, dy: 0 });
			setPointerActiveDirection(null);
			onDriveChange?.(null);
			onDriveEnd?.();
		},
		[onDriveChange, onDriveEnd, onPointerInteractionChange],
	);

	return (
		<div
			ref={containerRef}
			className={cn(
				!disabled && "cursor-grab active:cursor-grabbing",
				disabled && "pointer-events-none opacity-50",
				className,
			)}
			style={{ width: CONTROL_SIZE_PX, height: CONTROL_SIZE_PX }}
			aria-hidden={disabled}
			aria-label="Move character"
			role="slider"
			tabIndex={disabled ? -1 : 0}
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={Math.round(
				resolveThumbDragMagnitude(
					thumbOffset.dx,
					thumbOffset.dy,
					MAX_DRAG_PX,
				) * 100,
			)}
			onPointerDown={handlePointerDown}
			onPointerMove={handlePointerMove}
			onPointerUp={finishDrag}
			onPointerCancel={finishDrag}
		>
			<ThumbMovementPadFace
				thumbOffset={thumbOffset}
				activeDirection={activeDirection}
				isDragging={isDragging}
			/>
		</div>
	);
}

/**
 * Kingshot-style tap-anywhere stick: pad appears at press origin, drives while
 * dragging, fades out on release. Uses hit-testing so chrome / stats above the
 * scene keep receiving taps (`data-scene-thumb-ignore` or native controls).
 */
export function EphemeralThumbMovementLayer({
	enabled,
	className,
	"data-testid": dataTestId = "ephemeral-thumb-movement-layer",
	interactionHostRef,
	onAnalogChange,
	onDriveChange,
}: EphemeralThumbMovementLayerProps) {
	const rootRef = useRef<HTMLDivElement>(null);
	const originRef = useRef<{ x: number; y: number } | null>(null);
	const pointerIdRef = useRef<number | null>(null);
	const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const draggingRef = useRef(false);

	const [origin, setOrigin] = useState<{ x: number; y: number } | null>(null);
	const [thumbOffset, setThumbOffset] = useState({ dx: 0, dy: 0 });
	const [activeDirection, setActiveDirection] =
		useState<ThumbMovementDirection | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [phase, setPhase] = useState<
		"hidden" | "enter-from" | "in" | "out"
	>("hidden");

	const onAnalogChangeRef = useRef(onAnalogChange);
	const onDriveChangeRef = useRef(onDriveChange);
	onAnalogChangeRef.current = onAnalogChange;
	onDriveChangeRef.current = onDriveChange;

	const clearExitTimer = useCallback(() => {
		if (exitTimerRef.current != null) {
			clearTimeout(exitTimerRef.current);
			exitTimerRef.current = null;
		}
	}, []);

	const zeroOutput = useCallback(() => {
		onAnalogChangeRef.current?.(null);
		onDriveChangeRef.current?.(null);
	}, []);

	const resetSession = useCallback(() => {
		draggingRef.current = false;
		originRef.current = null;
		pointerIdRef.current = null;
		setIsDragging(false);
		setThumbOffset({ dx: 0, dy: 0 });
		setActiveDirection(null);
		zeroOutput();
	}, [zeroOutput]);

	const beginExit = useCallback(() => {
		setPhase("out");
		clearExitTimer();
		exitTimerRef.current = setTimeout(() => {
			setOrigin(null);
			setPhase("hidden");
			exitTimerRef.current = null;
		}, EPHEMERAL_MOTION_MS);
	}, [clearExitTimer]);

	useEffect(() => {
		if (!enabled) {
			clearExitTimer();
			resetSession();
			setOrigin(null);
			setPhase("hidden");
		}
	}, [clearExitTimer, enabled, resetSession]);

	useEffect(() => {
		return () => {
			clearExitTimer();
			zeroOutput();
		};
	}, [clearExitTimer, zeroOutput]);

	useEffect(() => {
		if (!enabled) {
			return;
		}
		const host =
			interactionHostRef?.current ?? rootRef.current?.parentElement;
		if (!host) {
			return;
		}

		const onPointerDown = (event: PointerEvent) => {
			if (event.button !== 0 || draggingRef.current) {
				return;
			}
			if (
				!shouldStartThumbAtClientPoint(
					host,
					event.clientX,
					event.clientY,
				)
			) {
				return;
			}
			if (isThumbPointerBlockedTarget(event.target, host)) {
				return;
			}
			const rect = host.getBoundingClientRect();
			if (rect.width <= 0 || rect.height <= 0) {
				return;
			}
			clearExitTimer();
			const x = event.clientX - rect.left;
			const y = event.clientY - rect.top;
			originRef.current = { x, y };
			pointerIdRef.current = event.pointerId;
			draggingRef.current = true;
			setOrigin({ x, y });
			setThumbOffset({ dx: 0, dy: 0 });
			setActiveDirection(null);
			setIsDragging(true);
			// Mount already-blurred at scale 0.92, then scale in — never fade
			// opacity on the backdrop-filter plate (avoids sharp→blur flicker).
			setPhase("enter-from");
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					setPhase("in");
				});
			});
			zeroOutput();
			try {
				host.setPointerCapture(event.pointerId);
			} catch {
				/* ignore */
			}
			// Do not preventDefault — chrome outside host must keep clicks.
			// Capture is only taken after we've confirmed this is a scene hit.
		};

		const onPointerMove = (event: PointerEvent) => {
			if (
				!draggingRef.current ||
				pointerIdRef.current !== event.pointerId ||
				!originRef.current
			) {
				return;
			}
			const rect = host.getBoundingClientRect();
			const x = event.clientX - rect.left;
			const y = event.clientY - rect.top;
			const next = emitFromOffset(
				x - originRef.current.x,
				y - originRef.current.y,
				{
					onAnalogChange: onAnalogChangeRef.current,
					onDriveChange: onDriveChangeRef.current,
				},
			);
			setThumbOffset(next.offset);
			setActiveDirection(next.direction);
		};

		const onPointerUp = (event: PointerEvent) => {
			if (
				!draggingRef.current ||
				pointerIdRef.current !== event.pointerId
			) {
				return;
			}
			if (host.hasPointerCapture(event.pointerId)) {
				host.releasePointerCapture(event.pointerId);
			}
			resetSession();
			beginExit();
		};

		host.addEventListener("pointerdown", onPointerDown, true);
		host.addEventListener("pointermove", onPointerMove);
		host.addEventListener("pointerup", onPointerUp);
		host.addEventListener("pointercancel", onPointerUp);
		return () => {
			host.removeEventListener("pointerdown", onPointerDown, true);
			host.removeEventListener("pointermove", onPointerMove);
			host.removeEventListener("pointerup", onPointerUp);
			host.removeEventListener("pointercancel", onPointerUp);
		};
	}, [
		beginExit,
		clearExitTimer,
		enabled,
		interactionHostRef,
		resetSession,
		zeroOutput,
	]);

	if (!enabled) {
		return null;
	}

	return (
		<div
			ref={rootRef}
			data-testid={dataTestId}
			className={cn(
				"pointer-events-none absolute inset-0 z-30 touch-none",
				className,
			)}
			aria-hidden
		>
			{origin ? (
				<div
					className="pointer-events-none absolute"
					style={{
						left: origin.x,
						top: origin.y,
						transform: "translate(-50%, -50%)",
					}}
					data-testid="ephemeral-thumb-pad"
				>
					<ThumbMovementPadFace
						thumbOffset={thumbOffset}
						activeDirection={activeDirection}
						isDragging={isDragging}
						ephemeralPhase={phase}
					/>
				</div>
			) : null}
		</div>
	);
}
